angular.module('bribac.services.api', [
  'bribac.values',
  'bribac.translations',
  'bribac.entities.kinds',
  'bribac.entities.agentFactory',
  'bribac.services.authStorage',
  'bribac.services.permissions'
])

  .factory('api', function(
    $http,
    $q,
    API_BASE_URL,
    API_BASE_URL_WITHOUT_API,
    API_VERSION,
    OAUTH_BASE_URL,
    KINDS,
    ROLES,
    agentFactory,
    permissions,
    authStorage,
    state,
    setLocale
  ) {
    var refreshAuthTimeout

    var API_VERSIONED_BASE_URL = API_BASE_URL + '/v/' + API_VERSION

    function storeAuth(request) {
      if (request.config.data.rememberMe) {
        authStorage.setRememberMe(request.config.data.rememberMe)
      }
      authStorage.set(request.data)
      return request
    }

    function refreshAuth(refresh_token) {
      return $http({
        method: 'POST',
        url: OAUTH_BASE_URL + '/access_token',
        headers: {'Content-Type': 'application/x-www-form-urlencoded'},
        data: 'grant_type=refresh_token&refresh_token=' + refresh_token
      })
    }

    function startAuthRefresh(request) {
      var auth = request.data
      var rememberMe = JSON.parse(authStorage.getRememberMe())
      var setRefresh = !!auth && !!auth.refresh_token && ((rememberMe === undefined) || rememberMe)

      if (setRefresh == true) {
        // `auth.expires_in` is seconds,
        // `* 1000` converts it to milliseconds,
        // `* 0.8` gives us 80% of that time, because why not.
        var refreshTimeInMilliseconds = auth.expires_in * 1000 * 0.8

        refreshAuthTimeout = setTimeout(function() {
          refreshAuth(auth.refresh_token)
            .then(storeAuth)
            .then(startAuthRefresh)
        }, refreshTimeInMilliseconds)
      }

      return request
    }

    function checkRole(request) {
      if (request.data.roles[0] !== ROLES.SUBJECT) {
        return Promise.resolve(storeAuth(request))
          .then(startAuthRefresh)
      } else {
        return request
      }
    }

    function throwOnInvalidKind(kind) {
      if (KINDS.indexOf(kind) < 0) {
        throw new TypeError('Unknown kind: ' + String(kind))
      }
    }

    function throwOnInvalidId(id) {
      if (typeof id !== 'number' || !isFinite(id)) {
        throw new TypeError('ID must be a finite number.')
      }
    }

    function throwOnInvalidToken(token) {
      if (!token || typeof token !== 'string') {
        throw new TypeError('Token must be a non-empty string.')
      }
    }

    function throwOnInvalidFilter(filter) {
      if (filter === undefined || filter === null) {
        throw new TypeError('Filter must be defined')
      }
    }

    /*
      Public methods.
    */

    var api = {

      // Auth.

      login: function(credentials) {
        return $http.post(API_BASE_URL + '/login', credentials)
          .then(checkRole)
      },

      finishLogin: function(request) {
        storeAuth(request);
        startAuthRefresh(request);
        return this.loadDataAfterAuthSuccess();
      },

      loadDataAfterAuthSuccess: function() {
        return api.getMe().then(
          function success(request) {
            var agent = request.data
            state.currentAgent = agent
            state.lastSelectedFilter = {}
            state.currentAgent.permissions = permissions.getPermissionsForRole(agent.roles[0])
            if (agent.locale) {
              setLocale(agent.locale)
            }
            return request
          }
        )
      },

      restoreSession: function() {
        var auth = authStorage.get()

        if (auth && auth.refresh_token) {
          return refreshAuth(auth.refresh_token)
            .catch(function(request) {
              authStorage.clear()
              throw request
            })
            .then(storeAuth)
            .then(startAuthRefresh)
            .then(this.loadDataAfterAuthSuccess)
        }

        return $q.reject('No session to restore.')
      },

      logout: function() {
        clearTimeout(refreshAuthTimeout)
        authStorage.clear()
      },

      // Password.

      forgotPassword: function(email) {
        return $http.post(API_BASE_URL_WITHOUT_API + '/password/forgot', {
          email: email
        })
      },

      verifyPasswordToken: function(token) {
        throwOnInvalidToken(token)
        return $http.get(API_BASE_URL_WITHOUT_API + '/password/validateToken?token=' + encodeURIComponent(token))
      },

      setPassword: function(token, password) {
        throwOnInvalidToken(token)
        return $http.post(API_BASE_URL_WITHOUT_API + '/password/set', {
          token: token,
          password: password
        })
      },

      // Entities.

      put: function(kind, entity) {
        throwOnInvalidKind(kind)
        var url = API_VERSIONED_BASE_URL + '/' + kind + '/' + entity.id
        return $http.put(url, entity)
      },

      post: function(kind, entity) {
        throwOnInvalidKind(kind)
        var url = API_VERSIONED_BASE_URL + '/' + kind
        return $http.post(url, entity)
      },

      get: function(kind, id) {
        throwOnInvalidKind(kind)
        if (id !== undefined) {
          throwOnInvalidId(id)
        }
        var url = API_VERSIONED_BASE_URL + '/' + kind
        if (id) url += '/' + id
        return $http.get(url)
      },

      delete: function(kind, id) {
        throwOnInvalidKind(kind)
        throwOnInvalidId(id)
        var url = API_VERSIONED_BASE_URL + '/' + kind + '/' + id
        return $http.delete(url)
      },

      getFilteredByOrg: function(kind, filter) {
        throwOnInvalidFilter(filter)
        var url = API_VERSIONED_BASE_URL + '/' + kind + filter
        return $http.get(url)
      },

      getMe: function() {
        return $http.get(API_VERSIONED_BASE_URL + '/me')
      },

      getView: function(view, id) {
        if (!view || typeof view !== 'string') {
          throw new TypeError('View must be a non-empty string.')
        }
        if (id !== undefined) {
          throwOnInvalidId(id)
        }
        var url = API_VERSIONED_BASE_URL + '/view/' + view
        if (id) url += '/' + id
        return $http.get(url)
      },

      getSubjectView: function(view, id, expandedTests, expandedSamples) {
        if (!view || typeof view !== 'string') {
          throw new TypeError('View must be a non-empty string.')
        }
        if (id !== undefined) {
          throwOnInvalidId(id)
        }
        var url = API_VERSIONED_BASE_URL + '/view/' + view
        if (id) url += '/' + id
        url += '?expandedTests=' + expandedTests
        url += '&expandedSamples=' + expandedSamples
      
        return $http.get(url)
      },
      getSubjectViewWithDateRange: function(view, id, expandedTests, expandedSamples, startDate, endDate) {
        if (!view || typeof view !== 'string') {
          throw new TypeError('View must be a non-empty string.')
        }
        if (id !== undefined) {
          throwOnInvalidId(id)
        }
        var url = API_VERSIONED_BASE_URL + '/view/' + view
        url+="/criteria"
        if (id) 
          url += '/' + id

        url += '?expandedTests=' + expandedTests
        url += '&expandedSamples=' + expandedSamples
        
        if(startDate === null || startDate === undefined){
          startDate = moment().startOf('month').toDate(); 
        }

        if(endDate === null || endDate === undefined) {
          endDate = moment().endOf('month').toDate()
        }
        startDate = moment(startDate).subtract(24, 'hours').toDate();
        function formatDate(date) {
          return moment(date).format('YYYY-MM-DD-HH:mm:ss');  
        }
        const formattedStartDate = formatDate(startDate);
        const formattedEndDate = formatDate(endDate);
        url += `&from=${formattedStartDate}&to=${formattedEndDate}`
        
        return $http.get(url)
      },
      search: function(data) {
        return $http.post(API_VERSIONED_BASE_URL + '/search', data)
      },

      createImmediateTest: function(subjectId) {
        throwOnInvalidId(subjectId)
        return $http.post(API_VERSIONED_BASE_URL + '/subject/immediateTest/' + subjectId, {})
      },

      getAssignableAgentRoles: function() {
        return $http.get(API_VERSIONED_BASE_URL + '/agent/assignableRoles')
      },

      report: function(method, config) {
        return $http.post(API_VERSIONED_BASE_URL + '/report/' + method, config, {responseType:'arraybuffer'})
      },

      putOrganizationSetting: function(setting) {
        return $http.put(API_VERSIONED_BASE_URL + '/organization/settings/' + setting.id, setting)
      },

      getAlcoholSamplesByPeriod: function (subjectId, dateFrom, dateTo) {
        throwOnInvalidId(subjectId);
        return $http.get(API_VERSIONED_BASE_URL + '/view/alcoholSamplesFromTo/' + subjectId + '?dateFrom=' + dateFrom + '&dateTo=' + dateTo)
      },

      getAlcoholSamplesChartDataFromTo: function (subjectId, dateFrom, dateTo, type) {
        throwOnInvalidId(subjectId);
        return $http.get(API_VERSIONED_BASE_URL + '/subject/'+subjectId+'/alcoholSamplesChartData?dateFrom=' + dateFrom + '&dateTo=' + dateTo + '&type=' + type)
      },

      getAlcoholSamplesChartData: function (subjectId) {
        throwOnInvalidId(subjectId);
        return $http.get(API_VERSIONED_BASE_URL + '/subject/'+subjectId+'/alcoholSamplesChartData/')
      },

      getAlertsView: function(typeFilter, offset, limit, startDate, endDate, subject, subGroup, mainGroup, iBacMac, orgId, order, orderDirection) {
        let baseUrl = API_VERSIONED_BASE_URL + '/view/' + KINDS.ALERTS
        const params = [
          `type=${typeFilter}`,
          `offset=${offset}`,
          `limit=${limit}`,
          startDate && `startDate=${startDate}`,
          endDate && `endDate=${endDate}`,
          subject && `subject=${subject}`,
          subGroup && `subGroup=${subGroup}`,
          mainGroup && `mainGroup=${mainGroup}`,
          iBacMac && `iBacMac=${iBacMac}`,
          orgId && `orgId=${orgId}`,
          order && `order=${order}`,
          orderDirection && `orderDirection=${orderDirection}`
        ].filter(Boolean).join('&');
        return $http.get(`${baseUrl}?${params}`)
      },

      mfaCode: function(data) {
        return $http.post(API_BASE_URL + '/mfaCode', data)
      },

      meMfa: function(data) {
        return $http.post(API_VERSIONED_BASE_URL + '/me/mfa', data)
      },

      getBoostImages: function(kind) {
        var url = API_VERSIONED_BASE_URL + '/' + kind
        return $http.get(url)
      },

      getInactiveDays: function(subjectId, fromDate, toDate) {
        var url = API_VERSIONED_BASE_URL + "/" + KINDS.SUBJECT + "/" + subjectId + "/" + KINDS.INACTIVE_DAYS + "?from="+ fromDate +"&to=" + toDate
        return $http.get(url)
      },

      addInactiveDay: function(subjectId, data) {
        var url = API_VERSIONED_BASE_URL + "/" + KINDS.SUBJECT + "/" + subjectId + "/" + KINDS.INACTIVE_DAYS
        return $http.post(url, data)
      },

      deleteInactiveDay: function(subjectId, inactiveDayId) {
        var url = API_VERSIONED_BASE_URL + "/" + KINDS.SUBJECT + "/" + subjectId + "/" + KINDS.DELETE_INACTIVE_DAYS
        return $http.post(url, inactiveDayId)
      },

      createManualClaim: function(claimData) {
        var url = API_VERSIONED_BASE_URL + '/claims/manual';
        return $http.post(url, claimData);
      },
    }

    return api
  })
